import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useRooftop } from '@store';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import { useGetPartsReturnsQuery } from '../queries/getPartsReturns.query.generated';
import { useUpdateDeliveryReturnStatusMutation } from '../queries/updateDeliveryReturnStatus.mutation.generated';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';

// HopDrive StatusChip pattern: filled 50-shade surface + dark (main/700) text,
// semantic colors only.
const STATUS_CHIPS: Record<string, { label: string; bg: string; fg: string }> = {
  initiated: { label: 'Initiated', bg: 'warning.50', fg: 'warning.700' },
  in_transit: { label: 'Inbound', bg: 'info.50', fg: 'info.700' },
  received_at_counter: { label: 'At counter', bg: 'success.50', fg: 'success.main' },
  closed: { label: 'Closed', bg: 'grey.100', fg: 'text.secondary' },
};

const FALLBACK_CHIP = { bg: 'grey.100', fg: 'text.secondary' };

const CHIP_SX = { fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: 11, height: 22 } as const;
const MONO_SX = { fontFamily: '"JetBrains Mono", monospace', fontSize: 13 } as const;
const HEADER_SX = {
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase',
  fontSize: 11,
  letterSpacing: '0.04em',
} as const;

const REASON_LABEL: Record<string, string> = {
  warranty: 'Warranty defect',
  wrong_part: 'Wrong part',
  customer_initiated: 'Customer-initiated',
  other: 'Other',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in_transit', label: 'Inbound' },
  { value: 'received_at_counter', label: 'At counter' },
  { value: 'closed', label: 'Closed' },
];

/** The Returns tab of the Parts Delivery shell: parts coming back to the counter, by status. The
 *  layout owns the tab strip and the header; this tab contributes its refresh as the header's sync. */
const ReturnsTab: React.FC = () => {
  const rooftop = useRooftop();
  const customerId = rooftop?.id || 0;
  // Marking a return received is managing existing work; gated on the Parts Delivery product's
  // manage capability.
  const canManage = capabilityGate(useProductEntry('parts-delivery'), 'manageExisting').kind === 'render';

  const { data, loading, error, refetch } = useGetPartsReturnsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const [markReceived] = useUpdateDeliveryReturnStatusMutation();

  const [statusFilter, setStatusFilter] = React.useState('all');
  const [receiving, setReceiving] = React.useState<number | null>(null);
  // The return awaiting "received at counter" confirmation (drives the modal).
  const [confirmRow, setConfirmRow] = React.useState<any | null>(null);

  const allReturns = (data?.delivery_returns ?? []) as any[];
  const inboundCount = allReturns.filter(r => r.status === 'in_transit').length;
  const returns = statusFilter === 'all' ? allReturns : allReturns.filter(r => r.status === statusFilter);

  const confirmLabel = (row: any) =>
    row?.return_part_number || row?.original_order?.order_number || (row ? `#${row.id}` : '');

  const closeConfirm = () => {
    if (receiving === null) setConfirmRow(null);
  };

  const handleConfirmReceive = async () => {
    if (!confirmRow) return;
    const row = confirmRow;
    setReceiving(row.id);
    try {
      const res = await markReceived({
        variables: { id: row.id, status: 'received_at_counter', now: new Date().toISOString() },
      });
      if (res.data?.update_delivery_returns_by_pk) {
        toast.success('Return marked received at counter.', { toastId: 'parts-delivery-return-receive' });
        setConfirmRow(null);
        refetch();
      } else {
        toast.error('Could not update the return.', { toastId: 'parts-delivery-return-receive' });
      }
    } catch (err: any) {
      toast.error(`Failed to mark received: ${err?.message ?? 'Unknown error'}`, { toastId: 'parts-delivery-return-receive' });
    } finally {
      setReceiving(null);
    }
  };

  // The tab's contribution to the header: its refresh.
  const tabActions = React.useMemo(() => ({ sync: refetch }), [refetch]);
  useShellTabActions(tabActions);

  // The status filter is a control: it sits in the tab content, above the list.
  const statusSelect = (
    <TextField
      select
      size='small'
      label='Status'
      value={statusFilter}
      onChange={e => setStatusFilter(e.target.value)}
      sx={{ minWidth: 160 }}
    >
      {STATUS_FILTERS.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.value === 'in_transit' && inboundCount ? `${opt.label} (${inboundCount})` : opt.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>{statusSelect}</Box>
        {inboundCount > 0 ? (
          <Card variant='custom' sx={{ p: 1.5, mb: 2, bgcolor: 'info.50' }}>
            <Typography variant='body2' sx={{ fontWeight: 600, color: 'info.700' }}>
              {inboundCount} return{inboundCount !== 1 ? 's' : ''} inbound to the counter
            </Typography>
          </Card>
        ) : null}

        {loading && !data ? <DefaultFallback severity='info' message='Loading returns...' /> : null}
        {error ? <DefaultFallback severity='error' message='Failed to load returns.' /> : null}

        <Card variant='custom' style={{ overflow: 'hidden' }}>
          {returns.length ? (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEADER_SX}>Part #</TableCell>
                  <TableCell sx={HEADER_SX}>Order #</TableCell>
                  <TableCell sx={HEADER_SX}>Reason</TableCell>
                  <TableCell sx={HEADER_SX}>Driver</TableCell>
                  <TableCell sx={HEADER_SX}>Initiated</TableCell>
                  <TableCell align='center' sx={HEADER_SX}>Status</TableCell>
                  <TableCell align='center' sx={HEADER_SX}>Signature</TableCell>
                  <TableCell align='right' sx={HEADER_SX}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returns.map((r: any) => {
                  const cfg = STATUS_CHIPS[r.status] ?? { ...FALLBACK_CHIP, label: r.status };
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={MONO_SX}>{r.return_part_number || '—'}</TableCell>
                      <TableCell sx={MONO_SX}>{r.original_order?.order_number || '—'}</TableCell>
                      <TableCell>{REASON_LABEL[r.return_reason] ?? r.return_reason ?? '—'}</TableCell>
                      <TableCell>{r.run?.driver?.user?.display_name || '—'}</TableCell>
                      <TableCell>{r.requested_at ? dayjs(r.requested_at).format('MMM D, h:mm A') : '—'}</TableCell>
                      <TableCell align='center'>
                        <Chip label={cfg.label} size='small' sx={{ ...CHIP_SX, bgcolor: cfg.bg, color: cfg.fg }} />
                      </TableCell>
                      <TableCell align='center'>
                        {r.return_signature_url ? (
                          <Box
                            component='a'
                            href={r.return_signature_url}
                            target='_blank'
                            rel='noopener'
                            sx={{ display: 'inline-block' }}
                          >
                            <Box
                              component='img'
                              src={r.return_signature_url}
                              alt='Return signature'
                              loading='lazy'
                              sx={{ height: 32, maxWidth: 90, objectFit: 'contain', bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: 0.5 }}
                            />
                          </Box>
                        ) : (
                          <Typography variant='caption' color='text.disabled'>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        {r.status === 'in_transit' && canManage ? (
                          <Button
                            size='small'
                            variant='contained'
                            disabled={receiving === r.id}
                            onClick={() => setConfirmRow(r)}
                            sx={{ textTransform: 'none', fontSize: 12 }}
                          >
                            {receiving === r.id ? 'Saving…' : 'Mark received'}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : !loading ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>No returns</Typography>
              <Typography variant='body2' color='text.secondary'>
                Returns initiated by drivers at delivery stops will appear here.
              </Typography>
            </Box>
          ) : null}
        </Card>
      </Box>

      <Dialog open={!!confirmRow} onClose={closeConfirm} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Mark return received?</DialogTitle>
        <DialogContent>
          <Typography variant='body1'>
            Confirm that return{' '}
            <Box component='span' sx={{ fontWeight: 700 }}>
              {confirmLabel(confirmRow)}
            </Box>{' '}
            has arrived at the counter. This updates its status to “At counter”.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={closeConfirm} disabled={receiving !== null}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirmReceive}
            disabled={receiving !== null}
          >
            {receiving !== null ? 'Saving…' : 'Mark received'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReturnsTab;
